/**
 * CompletePanel (13.2-09; D-13.2-A-1, D-13.2-P-3): "Complete the report", the `complete` verb through `VerbForm`.
 *
 * A Care Manager (or Oversight) adds what an incident's report still lacks after lodge, an intake stub included, under
 * their own name. The engine is 13.2-03's `buildComplete`; this panel only lays the form out:
 *
 * - **Still to add (N):** the report fields the record still lacks (`missingLodgeColumns`, the one definition triage's
 *   gate and Today's lens share), first, with the count of those still blank in the form. A field that only applies
 *   once one of them is answered (the harm's impact, the injured worker's name) joins them.
 * - **Already on the report:** every other report field, editable, so a wrong answer can be corrected.
 * - **A ShiftCare report** (`ExternalId` set): one line says where it came from, and `ReportedOn` is shown read only.
 *   The reconcile matches the row by `ExternalId` and filters it by `ReportedOn`, so neither can change here; the
 *   reporter (`ReportedByEmail`) is never offered at all (`PROVENANCE_LOCKED`).
 * - **`InjuredWorkerUPN`** (FieldLink's "I am the injured worker") is never an input. When it holds an address, the
 *   panel shows it with a "Clear it" control, the one write the dashboard may make to it (`CLEAR_ONLY`, D-13.2-P-2).
 * - **Partial saves** are fine: nothing here is required to save, and each refusal comes back at its field, focused
 *   (VerbForm's pattern). A field that only applies when another answer holds shows only then, unless the record
 *   already holds a value for it (so a stale value can be seen and cleared).
 */

import { useMemo } from 'react';
import VerbForm from '../VerbForm';
import type { VerbControlProps } from '../VerbForm';
import { Button } from '../../ui';
import { CLIENT_STREAMS, fieldOf } from '../../../config/fieldset/registerFields';
import { classifyInstant, formatDay, formatInstant } from '../../../lib/fieldValues';
import { lodgeConditions } from '../../../lib/lodgeBuilder';
import { PICKER_COLUMNS, REPORTED_ON, ruleHolds } from '../../../lib/lodgeLayout';
import { missingLodgeColumns } from '../../../lib/reportCompleteness';
import { CLEAR_ONLY, isBlank } from '../../../lib/recordVerbs';
import { verbInputColumns } from '../../../lib/verbForm';
import type { FormValues } from '../../../lib/verbForm';
import type { RecordRow } from '../../../types/records';
import type { PanelProps } from './registry';
import styles from '../record.module.css';

const I = 'incidents';
/** The intake's ticket number (the reconcile key, D-13.2-P-3). */
const EXTERNAL_ID = 'ExternalId';
const INJURED_WORKER_NAME = 'InjuredWorkerName';
const NAME_HINT = 'Their name, as ShiftCare or Harmony AP has it, never an email address.';

const ONLY_WHEN: ReadonlySet<string> = new Set(lodgeConditions(I).onlyWhen);
const CLEAR_ONLY_COLUMNS: ReadonlySet<string> = new Set(Object.keys(CLEAR_ONLY[I] ?? {}));

/** A stored day-or-instant in words: with its Melbourne time when it carries one. */
function whenText(value: unknown): string {
  if (isBlank(value)) return 'Not recorded';
  const text = String(value);
  return classifyInstant(text) === 'time-of-day' ? formatInstant(text) : formatDay(text);
}

/** Is this an intake row (it carries the ShiftCare ticket number)? */
function isIntakeRow(row: RecordRow): boolean {
  return !isBlank(row.fields[EXTERNAL_ID]);
}

interface CompleteLayout {
  /** Every column the form shows, in order: the "Still to add" group, then the rest. */
  columns: string[];
  /** The "Still to add" group (a subset of `columns`, at its head). */
  toAdd: ReadonlySet<string>;
}

/**
 * The form's columns, grouped (pure). `ReportedOn` is left out on an intake row: it is shown
 * read only in the lead instead.
 */
function completeLayout(row: RecordRow): CompleteLayout {
  const intake = isIntakeRow(row);
  const all = verbInputColumns(I, 'complete').filter((c) => !(intake && c === REPORTED_ON));
  const missing = new Set(missingLodgeColumns(row).map((spec) => spec.name));
  const toAdd = new Set<string>();
  let previous: string | null = null;
  for (const column of all) {
    const rule = fieldOf(I, column)?.required.lodge;
    const follows =
      // a field that applies once a missing answer is given (HarmDescription after HarmKind) joins it
      (ONLY_WHEN.has(column) && typeof rule === 'object' && toAdd.has(rule.when) && isBlank(row.fields[column])) ||
      // the clear-only FieldLink address stays beside the injured worker's name
      (CLEAR_ONLY_COLUMNS.has(column) && previous !== null && toAdd.has(previous));
    if (missing.has(column) || follows) toAdd.add(column);
    previous = column;
  }
  return { columns: [...all.filter((c) => toAdd.has(c)), ...all.filter((c) => !toAdd.has(c))], toAdd };
}

/** Does a column show for the current answers? (See the header.) */
function shows(row: RecordRow, column: string, values: FormValues): boolean {
  const held = !isBlank(row.fields[column]);
  if (CLEAR_ONLY_COLUMNS.has(column)) return held;
  if (ONLY_WHEN.has(column)) return held || ruleHolds(fieldOf(I, column)?.required.lodge, values);
  if (PICKER_COLUMNS.includes(column)) return held || CLIENT_STREAMS.includes(values.BusinessUnit ?? '');
  return true;
}

/** FieldLink's address: shown, never typed; "Clear it" is the one write (CLEAR_ONLY). */
function ClearOnlyControl({ control, stored }: { control: VerbControlProps; stored: string }) {
  const kept = control.value.trim() !== '';
  // The id sits on the group, not the button: the field's label would otherwise rename the button. A refusal can
  // still focus it (tabIndex -1).
  return (
    <div id={control.id} className={styles.clearOnly} tabIndex={-1}>
      <p className={styles.clearOnlyLine}>
        {kept ? (
          <>
            Injured worker recorded by email: <strong>{stored}</strong>
          </>
        ) : (
          <>
            <strong>{stored}</strong> will be cleared when you save.
          </>
        )}
      </p>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        aria-describedby={control.describedBy}
        onClick={() => control.set(kept ? '' : stored)}
      >
        {kept ? 'Clear it' : 'Keep it'}
      </Button>
      <p className={styles.hint}>
        FieldLink records this when the person reporting says they were the injured worker. If that isn&rsquo;t who was
        injured, clear it and name the worker in &ldquo;{fieldOf(I, INJURED_WORKER_NAME)?.label ?? INJURED_WORKER_NAME}&rdquo;.
      </p>
    </div>
  );
}

export default function CompletePanel({ row, siblings, actor, onSaved }: PanelProps) {
  const layout = useMemo(() => completeLayout(row), [row]);
  const intake = isIntakeRow(row);
  const visible = (values: FormValues) => layout.columns.filter((c) => shows(row, c, values));

  return (
    <VerbForm
      register={row.register}
      verb="complete"
      row={row}
      siblings={siblings}
      actor={actor}
      columns={layout.columns}
      saveLabel="Save the report"
      onSaved={onSaved}
      showColumn={(column, values) => shows(row, column, values)}
      renderControl={(column, control) =>
        CLEAR_ONLY_COLUMNS.has(column) ? <ClearOnlyControl control={control} stored={String(row.fields[column] ?? '')} /> : null
      }
      fieldNote={(column) => (column === INJURED_WORKER_NAME ? <p className={styles.hint}>{NAME_HINT}</p> : null)}
      beforeField={(column, values) => {
        const shown = visible(values);
        const firstToAdd = shown.find((c) => layout.toAdd.has(c));
        const firstRest = shown.find((c) => !layout.toAdd.has(c));
        if (column === firstToAdd) {
          const left = shown.filter((c) => layout.toAdd.has(c) && !CLEAR_ONLY_COLUMNS.has(c) && (values[c] ?? '').trim() === '').length;
          return (
            <div className={styles.groupHead}>
              <h4 className={styles.groupTitle}>Still to add ({left})</h4>
              <p className={styles.hint}>What the report is missing. You can save part of it now and add the rest later.</p>
            </div>
          );
        }
        if (column === firstRest) {
          return (
            <div className={styles.groupHead}>
              <h4 className={styles.groupTitle}>Already on the report</h4>
              <p className={styles.hint}>Correct anything that isn&rsquo;t right. Each change goes on the trail under your name.</p>
            </div>
          );
        }
        return null;
      }}
      lead={
        intake ? (
          <div className={styles.provenance}>
            <p className={styles.provenanceLine}>
              From the ShiftCare report {String(row.fields[EXTERNAL_ID])}. The reporter and the ShiftCare report time are kept
              as ShiftCare sent them.
            </p>
            <p className={styles.provenanceLine}>
              <span className={styles.label}>{fieldOf(I, REPORTED_ON)?.label ?? REPORTED_ON}:</span> {whenText(row.fields[REPORTED_ON])}{' '}
              <span className={styles.hint}>(read only)</span>
            </p>
          </div>
        ) : null
      }
    />
  );
}
